// src/components/calendar/OutlookCalendarWeekDayView.jsx
import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isoWeek from "dayjs/plugin/isoWeek";
import "dayjs/locale/es";
import { Dropdown, Menu } from "antd";
import ScrollDatePicker from "./ScrollDatePicker";
import { SyncOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  buildDaysForView,
  eventKey,
  groupEventsByDay,
  getTribunalDisplayName,
} from "./boardUtils";

dayjs.extend(utc);
dayjs.extend(isoWeek);

// ─── City / badge config ─────────────────────────────────────────────────────
const CITY_CFG = {
  2641: { label: "SLRC", bg: "#dbeafe", color: "#1d4ed8", dot: "#3b82f6", colorKey: "sw-blue"    },
  700:  { label: "MXL",  bg: "#f3e8ff", color: "#7e22ce", dot: "#a855f7", colorKey: "sw-black"   },
  703:  { label: "TIJ",  bg: "#fce7f3", color: "#be185d", dot: "#ec4899", colorKey: "sw-wine"    },
  2633: { label: "PÑS",  bg: "#dcfce7", color: "#15803d", dot: "#22c55e", colorKey: "sw-penasco" },
  699:  { label: "ENS",  bg: "#cffafe", color: "#0e7490", dot: "#06b6d4", colorKey: "sw-ensenada" },
};
const PAGOS_CFG = { label: "PAGO", bg: "#fee2e2", color: "#dc2626", dot: "#ef4444", colorKey: "sw-red"    };
const OTHER_CFG = { label: "OTRO", bg: "#ede9fe", color: "#6d28d9", dot: "#a855f7", colorKey: "sw-purple" };

const BADGE_ORDER = [
  { colorKey: "sw-blue",     label: "SLRC", bg: "#dbeafe", color: "#1d4ed8", dot: "#3b82f6" },
  { colorKey: "sw-black",    label: "MXL",  bg: "#f3e8ff", color: "#7e22ce", dot: "#a855f7" },
  { colorKey: "sw-wine",     label: "TIJ",  bg: "#fce7f3", color: "#be185d", dot: "#ec4899" },
  { colorKey: "sw-penasco",  label: "PÑS",  bg: "#dcfce7", color: "#15803d", dot: "#22c55e" },
  { colorKey: "sw-ensenada", label: "ENS",  bg: "#cffafe", color: "#0e7490", dot: "#06b6d4" },
  { colorKey: "sw-purple",   label: "OTRO", bg: "#ede9fe", color: "#6d28d9", dot: "#a855f7" },
  { colorKey: "sw-red",      label: "PAGO", bg: "#fee2e2", color: "#dc2626", dot: "#ef4444" },
];

const TYPE_LABELS = {
  1: "Audiencia",
  2: "Evento",
  3: "Pago",
  4: "Emisión Citatorio",
  5: "Notif. Citatorio",
  6: "Baja",
  7: "Baja",
  8: "Pendiente",
  9: "Reunión",
};

// Used for header badge counting/filtering (PAGO is its own badge)
function getCityBadge(ev) {
  if (Number(ev?.id_agenda_evento) === 3) return PAGOS_CFG;
  const cid = Number(ev?.ciudad_id);
  return CITY_CFG[cid] || OTHER_CFG;
}

// Used for row display — always shows real city, never "PAGO"
function getCityForRow(ev) {
  const cid = Number(ev?.ciudad_id);
  return CITY_CFG[cid] || OTHER_CFG;
}

function getModulo(ev) {
  const src = (ev?.source_table || "").toLowerCase();
  if (src.includes("tribunal")) return "Tribunal";
  if (src.includes("junta")) return "Junta de Conciliación";
  if (src.includes("conciliacion") || src.includes("conciliación")) return "Centro de Conciliación";
  if (src.includes("desvinculacion") || src.includes("desvinculación")) return "Desvinculación";
  return "—";
}

function getTipo(ev) {
  const src = (ev?.source_table || "").toLowerCase();
  if (src.includes("tribunal")) {
    // Etapas 10/11/12 son audiencias; el resto (1-9, 13-15) son plazos/términos procesales.
    if (src === "tribunal_conciliatoria") return "AUDIENCIA CONCILIATORIA";
    if (src === "tribunal_preliminar" || src === "tribunal_audiencia_11") return "AUDIENCIA PRELIMINAR";
    if (src === "tribunal_audiencia_12") return "AUDIENCIA DE JUICIO";
    if (src === "tribunal_terminacion" || src === "tribunal_constancia") return "PAGO";
    // Fallback: parse title for older events
    const title = (ev?.title || "").toLowerCase();
    if (title.includes("concili")) return "AUDIENCIA CONCILIATORIA";
    if (title.includes("prelim")) return "AUDIENCIA PRELIMINAR";
    if (title.includes("juicio")) return "AUDIENCIA DE JUICIO";
    if (title.includes("convenio") || title.includes("parcialidad") || title.includes("constancia")) return "PAGO";
    return "TÉRMINOS";
  }
  if (src === "desvinculaciones") return "BAJA";
  if (src === "desvinculaciones_convenios") return "PAGO";
  const idEvento = Number(ev?.id_agenda_evento);
  if (idEvento === 3) return "PAGO";
  if (idEvento === 1 && (src.includes("conciliacion") || src.includes("conciliación"))) return "AUD. PREJUDICIAL";
  return (TYPE_LABELS[idEvento] || "Evento").toUpperCase();
}

// ─── TIPO colors — same palette as the KPI cards in Tribunal ────────────────
const TIPO_COLORS = {
  critico:   { bg: "#FEE2E2", fg: "#DC2626" }, // Términos Críticos
  audiencia: { bg: "#FEF3C7", fg: "#F59E0B" }, // Próximas Audiencias
  concluido: { bg: "#16A34A26", fg: "#16A34A" }, // Concluidos
  default:   { bg: "#DCE9F5", fg: "#0A539A" }, // Activos
};

function getTipoColor(tipo) {
  const t = (tipo || "").toUpperCase();
  if (t === "TÉRMINOS") return TIPO_COLORS.critico;
  if (t === "PAGO") return TIPO_COLORS.concluido;
  if (t.startsWith("AUDIENCIA") || t === "AUD. PREJUDICIAL") return TIPO_COLORS.audiencia;
  return TIPO_COLORS.default;
}

function getDisplayTitle(ev) {
  const src = (ev?.source_table || "").toLowerCase();
  if (src.startsWith("tribunal")) return getTribunalDisplayName(ev);
  return ev?.title || "";
}

function fmtAmPm(iso) {
  return dayjs(iso).format("hh:mm A");
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Column proportions — shared between header row and data rows
const COLS = "120px 80px 1fr 160px 180px";

// ─── Single event row ─────────────────────────────────────────────────────────
function EventRow({ ev, highlighted, isPast, openPeek, onEditEvent, onDeleteEvent }) {
  const badge = getCityForRow(ev);
  const tipo = getTipo(ev);
  const tipoColor = getTipoColor(tipo);

  const menu = (
    <Menu
      items={[
        {
          key: "edit",
          icon: <SyncOutlined />,
          label: "Editar",
          onClick: () => onEditEvent?.(ev),
        },
        {
          key: "del",
          icon: <DeleteOutlined />,
          danger: true,
          label: "Eliminar",
          onClick: () => onDeleteEvent?.(ev),
        },
      ]}
    />
  );

  return (
    <Dropdown overlay={menu} trigger={["contextMenu"]}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: COLS,
          alignItems: "center",
          padding: "10px 16px",
          borderBottom: "1px solid #f3f4f6",
          background: highlighted ? "#FFFCE1" : isPast ? "#fafafa" : "transparent",
          borderLeft: highlighted ? "4px solid #FDE047" : "4px solid transparent",
          cursor: "pointer",
          transition: "background 0.15s",
          position: "relative",
          opacity: isPast ? 0.55 : 1,
        }}
        onClick={(e) => {
          e.stopPropagation();
          openPeek?.(ev, e.currentTarget);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onEditEvent?.(ev);
        }}
        onMouseEnter={(e) => {
          if (!highlighted) e.currentTarget.style.background = "#f9fafb";
        }}
        onMouseLeave={(e) => {
          if (!highlighted) e.currentTarget.style.background = "transparent";
        }}
      >
        {/* HORA */}
        <div style={{ fontWeight: 700, fontSize: 13, color: "#111827", whiteSpace: "nowrap" }}>
          {fmtAmPm(ev.start)}
        </div>

        {/* CIUDAD */}
        <div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "2px 8px",
              borderRadius: 9999,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.03em",
              background: badge.bg,
              color: badge.color,
            }}
          >
            {badge.label}
          </span>
        </div>

        {/* ASUNTO / EXPEDIENTE */}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: "#111827",
              marginBottom: ev.expediente ? 2 : 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getDisplayTitle(ev) || "(Sin título)"}
          </div>
          {ev.expediente && (
            <div style={{ fontSize: 11, color: "#9ca3af" }}>{ev.expediente}</div>
          )}
        </div>

        {/* TIPO */}
        <div style={{ paddingLeft: 8 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "2px 8px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.02em",
              background: tipoColor.bg,
              color: tipoColor.fg,
            }}
          >
            {tipo}
          </span>
        </div>

        {/* MÓDULO */}
        <div style={{ textAlign: "right", fontWeight: 600, fontSize: 12, color: "#374151", paddingRight: 4 }}>
          {getModulo(ev)}
        </div>
      </div>
    </Dropdown>
  );
}

// ─── NavArrow ────────────────────────────────────────────────────────────────
function NavArrow({ onClick, path }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "7px 10px",
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        background: "#fff",
        cursor: "pointer",
        color: "#9ca3af",
        display: "flex",
        alignItems: "center",
      }}
    >
      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d={path} />
      </svg>
    </button>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function OutlookCalendarWeekDayView({
  anchor,
  cursorDate,
  view,
  evs,
  peek,
  openPeek,
  closePeek,
  onSelectSlot,
  onEditEvent,
  onDeleteEvent,
  onPrev,
  onNext,
  onPickDate,
  filters,
  setFilters,
}) {
  const [calOpen, setCalOpen] = useState(false);
  const isDayView = view === "day";

  const daysForView = useMemo(() => buildDaysForView(anchor, view), [anchor, view]);

  const dayRange = useMemo(() => {
    const start = daysForView[0]?.startOf("day") ?? anchor.startOf("day");
    const end = (
      daysForView[daysForView.length - 1]?.startOf("day") ?? anchor.startOf("day")
    )
      .add(1, "day")
      .endOf("day");
    return { start, end };
  }, [daysForView, anchor]);

  const selectedColors = Array.isArray(filters?.colorTags) ? filters.colorTags : [];

  // Igual que la vista mes: filtrar por color en el cliente
  const displayEvs = useMemo(() => {
    if (!selectedColors.length) return evs;
    return (evs || []).filter((ev) => selectedColors.includes(getCityBadge(ev).colorKey));
  }, [evs, selectedColors]);

  const byDay = useMemo(
    () => groupEventsByDay({ evs: displayEvs, rangeStart: dayRange.start, rangeEnd: dayRange.end }),
    [displayEvs, dayRange.start.valueOf(), dayRange.end.valueOf()]
  );

  // Flat list de todos los eventos del rango (sin filtro) para contar los chips
  const allEvs = useMemo(() => {
    const full = groupEventsByDay({ evs, rangeStart: dayRange.start, rangeEnd: dayRange.end });
    const out = [];
    for (const arr of full.values()) out.push(...arr);
    return out;
  }, [evs, dayRange.start.valueOf(), dayRange.end.valueOf()]);

  // Per-city event counts (siempre desde evs completo para que los números no cambien al filtrar)
  const cityCounts = useMemo(() => {
    const map = {};
    for (const ev of allEvs) {
      const { colorKey } = getCityBadge(ev);
      map[colorKey] = (map[colorKey] || 0) + 1;
    }
    return map;
  }, [allEvs]);

  // Radio igual que mes: un solo chip activo, o todos si se deselecciona
  const toggleCity = (colorKey) => {
    if (!setFilters) return;
    setFilters((p) => {
      const cur = Array.isArray(p?.colorTags) ? p.colorTags : [];
      if (cur.length === 1 && cur[0] === colorKey) {
        return { ...(p || {}), colorTags: [] };
      }
      return { ...(p || {}), colorTags: [colorKey] };
    });
  };

  // ── Header date text ──────────────────────────────────────────────────────
  const dateText = useMemo(() => {
    if (isDayView) {
      const m = cap(anchor.locale("es").format("MMMM"));
      return `${anchor.format("D")} de ${m} de ${anchor.format("YYYY")}`;
    }
    const s = daysForView[0];
    const e = daysForView[daysForView.length - 1];
    const mE = cap(e.locale("es").format("MMMM"));
    const y  = e.format("YYYY");
    if (s.month() === e.month()) {
      return `${s.format("D")} - ${e.format("D")} ${mE} ${y}`;
    }
    const mS = cap(s.locale("es").format("MMMM"));
    return `${s.format("D")} ${mS} - ${e.format("D")} ${mE} ${y}`;
  }, [isDayView, anchor, daysForView]);

  const rightLabel = isDayView
    ? anchor.locale("es").format("dddd").toUpperCase()
    : "SEMANA";
  const rightNum = isDayView ? anchor.format("D") : anchor.isoWeek();

  const visibleBadges = BADGE_ORDER;

  // ── Render rows for a single day ──────────────────────────────────────────
  function renderDayContent(dayStart, dayEvs) {
    const isPastDay = dayStart.isBefore(dayjs(), "day");

    if (!dayEvs || dayEvs.length === 0) {
      return (
        <div
          style={{
            padding: "14px 16px",
            textAlign: "center",
            fontSize: 12,
            color: "#9ca3af",
            fontStyle: "italic",
          }}
        >
          Sin eventos programados
        </div>
      );
    }

    return dayEvs.map((ev) => (
      <EventRow
        key={eventKey(ev)}
        ev={ev}
        highlighted={false}
        isPast={isPastDay}
        openPeek={openPeek}
        onEditEvent={onEditEvent}
        onDeleteEvent={onDeleteEvent}
      />
    ));
  }

  // ── Styles ────────────────────────────────────────────────────────────────
  const colHeaderStyle = {
    display: "grid",
    gridTemplateColumns: COLS,
    padding: "8px 16px",
    borderBottom: "1px solid #f3f4f6",
    background: "#f9fafb",
  };
  const colLabelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)",
          border: "1px solid #f1f3f5",
          overflow: "hidden",
          minHeight: "100%",
        }}
      >
        {/* ── Card Header ──────────────────────────────────────────────── */}
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid #f3f4f6",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          {/* Left: summary text + filterable badges */}
          <div>
            <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 14 }}>
              {isDayView
                ? "Desglose cronológico de audiencias y citas."
                : "Desglose cronológico de audiencias y citas para la semana."}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              {visibleBadges.map((b) => {
                const active =
                  !selectedColors.length || selectedColors.includes(b.colorKey);
                return (
                  <button
                    key={b.colorKey}
                    onClick={() => toggleCity(b.colorKey)}
                    title={active ? `Ocultar ${b.label}` : `Mostrar ${b.label}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "5px 12px",
                      borderRadius: 9999,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background: b.bg,
                      color: b.color,
                      border: "none",
                      cursor: "pointer",
                      opacity: active ? 1 : 0.38,
                      transition: "opacity 0.2s",
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: b.dot,
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    {b.label}: {cityCounts[b.colorKey] || 0}
                  </button>
                );
              })}
              {allEvs.length > 0 && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "5px 12px",
                    borderRadius: 9999,
                    fontSize: 11,
                    fontWeight: 700,
                    background: "#f9fafb",
                    color: "#6b7280",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  TOTAL: {allEvs.length}
                </span>
              )}
            </div>
          </div>

          {/* Right: calendar date display + nav arrows + day/week number */}
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            {/* Calendar icon + date label → opens DatePicker */}
            <div style={{ position: "relative" }}>
              <ScrollDatePicker
                open={calOpen}
                value={anchor}
                onChange={(v) => { if (v) { onPickDate?.(v); setCalOpen(false); } }}
                onOpenChange={(o) => { if (!o) setCalOpen(false); }}
                style={{ position: "absolute", opacity: 0, width: 0, height: 0, pointerEvents: "none", top: "100%", left: 0 }}
                popupStyle={{ zIndex: 10001 }}
                allowClear={false}
              />
              <div
                onClick={() => setCalOpen(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  overflow: "hidden",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    background: "#2563eb",
                    padding: "8px 12px",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div
                  style={{
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#4b5563",
                    whiteSpace: "nowrap",
                  }}
                >
                  {dateText}
                </div>
              </div>
            </div>

            {/* Prev / Next */}
            <div style={{ display: "flex", gap: 4 }}>
              <NavArrow onClick={onPrev} path="M15 19l-7-7 7-7" />
              <NavArrow onClick={onNext} path="M9 5l7 7-7 7" />
            </div>

            {/* Day name / "SEMANA" + number */}
            <div
              style={{
                textAlign: "right",
                borderLeft: "1px solid #e5e7eb",
                paddingLeft: 20,
                minWidth: 60,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  marginBottom: 2,
                }}
              >
                {rightLabel}
              </div>
              <div
                style={{
                  fontSize: 42,
                  fontWeight: 900,
                  color: "#1e293b",
                  lineHeight: 1,
                }}
              >
                {rightNum}
              </div>
            </div>
          </div>
        </div>

        {/* ── Column headers ────────────────────────────────────────────── */}
        <div style={colHeaderStyle}>
          <div style={colLabelStyle}>Hora</div>
          <div style={colLabelStyle}>Ciudad</div>
          <div style={colLabelStyle}>Asunto / Expediente</div>
          <div style={colLabelStyle}>Tipo</div>
          <div style={{ ...colLabelStyle, textAlign: "right" }}>Módulo</div>
        </div>

        {/* ── Content rows ──────────────────────────────────────────────── */}
        {isDayView ? (
          renderDayContent(
            anchor.startOf("day"),
            byDay.get(anchor.format("YYYY-MM-DD")) ?? []
          )
        ) : (
          daysForView.map((d) => {
            const key = d.format("YYYY-MM-DD");
            const dayLabel = cap(d.locale("es").format("dddd D"));
            const isPastDay = d.startOf("day").isBefore(dayjs(), "day");
            return (
              <div key={key}>
                {/* Day separator */}
                <div
                  style={{
                    background: isPastDay ? "#f3f4f6" : "#f9fafb",
                    padding: "6px 16px",
                    borderTop: "1px solid #e5e7eb",
                    borderBottom: "1px solid #e5e7eb",
                    textAlign: "center",
                    opacity: isPastDay ? 0.6 : 1,
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: isPastDay ? "#9ca3af" : "#2563eb",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {dayLabel}
                  </span>
                </div>
                {renderDayContent(d.startOf("day"), byDay.get(key) ?? [])}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
